import { useLazyQuery } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { GetCohortQuery } from "generated-graphql/graphql";
import GET_DOCUMENT_DOWNLOAD_LINK_QUERY from "queries/getDocumentDownloadLink";
import { prettyPrintDateTime } from "util/dates";
import { downloadFromUrl } from "util/download";

type CohortDocument = NonNullable<
  NonNullable<
    NonNullable<GetCohortQuery["cohort"]>["currentWorkflow"]
  >["activities"][number]["documents"]
>[number];

export const CohortDocumentDisplay = ({ document: doc }: { document: CohortDocument }) => {
  const alerts = useAlerts();
  // Signed on click, not on render: this row can sit on a long-lived cohort
  // tab far past a render-time link's expiry, and a click on an expired link
  // is a plain navigation to S3's error XML instead of a download.
  const [getDownloadLink, { loading }] = useLazyQuery(GET_DOCUMENT_DOWNLOAD_LINK_QUERY, {
    fetchPolicy: "network-only",
  });

  const handleDownload = async () => {
    const { data, error } = await getDownloadLink({ variables: { id: doc.document.id } });
    const downloadLink = data?.getDocumentDownloadLink;
    if (!downloadLink) {
      alerts.error(error?.message ?? "The document could not be prepared for download.");
      return;
    }
    downloadFromUrl(downloadLink);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      key={doc.document.id}
      sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}
    >
      <Box width="100%">
        <Typography variant="body2" fontWeight="medium">
          {doc.document.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Updated: {prettyPrintDateTime(doc.document.updatedAt)}
          {doc.document.sizeMegabytes && ` | Size: ${doc.document.sizeMegabytes.toFixed(2)} MB`}
        </Typography>
        {doc.document.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {doc.document.description}
          </Typography>
        )}
      </Box>
      <IconButton size="small" disabled={loading} onClick={() => void handleDownload()}>
        <Download />
      </IconButton>
    </Stack>
  );
};
